import { GuideEditorPage } from '@/app/guide/GuideEditorPage';
import { GUIDE_DOTS } from '@/app/guide/guideDot';
import { GuidesTab } from '@/app/guide/GuidesTab';
import EquipmentFieldsTable from '@/app/itemType/EquipmentFieldsTable';
import EquipmentSpecsTable from '@/app/itemType/EquipmentSpecsTable';
import EquipmentTypeDetails from '@/app/itemType/EquipmentTypeDetails';
import { PurchaseLinksSection } from '@/app/itemType/PurchaseLinksSection';
import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav, type TabNavItem } from '@/components/TabNav';
import { Button } from '@/components/ui/button';
import { useRestoreEquipmentType } from '@/db/useRestoreEquipmentType';
import { equipmentTypeEditState, equipmentTypeState } from '@/state';
import { guideHealthState, guideRowsState } from '@/state/guide.state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDate } from '@/util/dates';
import type { EquipmentType, EquipmentTypeId } from '@rock/types';
import { type FC, useMemo } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom';

/**
 * One equipment type. The guide is a sibling of the tabbed shell rather than a pane inside it — the
 * ReactFlow canvas wants the whole viewport, so it renders full-bleed with its own back arrow.
 *
 * Everything here reads the STAGED type (draft if there is one, else live), so an unpublished type
 * has a page like any other.
 */
export const EquipmentTypePage: FC = () => {
    const { equipmentTypeId = '_' as EquipmentTypeId } = useParams<{ equipmentTypeId: EquipmentTypeId }>();
    const [type, typeLoadable] = useLoadableAtomValue(equipmentTypeEditState(equipmentTypeId));

    if (!type) {
        return (
            <PageShell>
                <PageHeader
                    title={typeLoadable.state === 'loading' ? 'Loading equipment type…' : 'Equipment type not found'}
                    backLabel="Back to equipment types"
                />
            </PageShell>
        );
    }

    return (
        <Routes>
            <Route path="guide" element={<Navigate replace to={`/equipment/types/${type.id}/guides`} />} />
            <Route path="guides/:flowId" element={<EquipmentGuideEditor type={type} />} />
            <Route path="*" element={<EquipmentTypeTabs type={type} />} />
        </Routes>
    );
};

const EquipmentGuideEditor: FC<{ type: EquipmentType }> = ({ type }) => {
    const { flowId } = useParams<{ flowId: string }>();
    const params = useTypeFlowParams(type);
    return <GuideEditorPage params={params} flowId={flowId} editBase={`/equipment/types/${type.id}/guides`} showBack />;
};

const EquipmentTypeTabs: FC<{ type: EquipmentType }> = ({ type }) => {
    const params = useTypeFlowParams(type);
    const guides = useAsyncAtomValue(guideRowsState(params), []);
    const health = useAsyncAtomValue(guideHealthState(params), 'none');
    const base = `/equipment/types/${type.id}`;
    // A system is a grouping of its parts, not a thing with a nameplate — the parts carry the fields
    // and specs. Same reason `systemParts` replaces the spec list on the catalog row.
    const isSystem = type.scope === 'system';

    const tabs: TabNavItem[] = [
        { label: 'Details', to: `${base}/details` },
        ...(isSystem
            ? []
            : [
                  { label: 'Fields', to: `${base}/fields` },
                  { label: 'Specs', to: `${base}/specs` },
              ]),
        // Purchase links only exist for consumables — see PurchaseLinksSection.
        ...(type.scope === 'supplies' ? [{ label: 'Purchase Links', to: `${base}/purchase-links` }] : []),
        { label: 'Guides', to: `${base}/guides`, count: guides.length, dot: GUIDE_DOTS[health] },
    ];

    return (
        <PageShell>
            <PageHeader title={type.title} id={type.id} backLabel="Back to equipment types" />

            <RetiredBanner typeId={type.id} />

            <TabNav tabs={tabs} />

            <Routes>
                <Route index element={<Navigate replace to="./details" />} />
                <Route path="details" element={<EquipmentTypeDetails typeId={type.id} />} />
                {/* Dropped for systems along with their tabs, so an old link lands on Details. */}
                {!isSystem && (
                    <Route path="fields" element={<EquipmentFieldsTable spaceId={type.spaceId} typeId={type.id} />} />
                )}
                {!isSystem && <Route path="specs" element={<EquipmentSpecsTable typeId={type.id} />} />}
                <Route path="purchase-links" element={<PurchaseLinksSection typeId={type.id} />} />
                <Route path="guides" element={<GuidesTab params={params} editBase={`${base}/guides`} />} />
                {/* An unknown sub-path used to render the shell with an empty body. */}
                <Route path="*" element={<Navigate replace to={`${base}/details`} />} />
            </Routes>
        </PageShell>
    );
};

/**
 * Only the live doc's retirement counts (a draft's copy may be missing or stale), so this reads live
 * rather than the staged copy the rest of the page shows.
 */
const RetiredBanner: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const live = useAsyncAtomValue(equipmentTypeState(typeId), undefined);
    const { busy, restoreEquipmentType } = useRestoreEquipmentType();
    if (!live?.retiredAt) return null;

    return (
        <div className="flex flex-row items-center justify-between gap-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <div>
                Retired on {formatDate(live.retiredAt)}, so it isn't offered for anything new.
                {live.replacedBy && (
                    <>
                        {' '}
                        Its equipment moved to <ReplacementLink typeId={live.replacedBy} />.
                    </>
                )}
            </div>
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => restoreEquipmentType(live)}
                disabled={busy}
            >
                Restore
            </Button>
        </div>
    );
};

const ReplacementLink: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const replacement = useAsyncAtomValue(equipmentTypeState(typeId), undefined);
    return (
        <Link to={`/equipment/types/${typeId}`} className="font-medium underline">
            {replacement?.title ?? typeId}
        </Link>
    );
};

/** Stable params object — the flow atom families key on it, so a fresh literal each render thrashes them. */
function useTypeFlowParams(type: EquipmentType) {
    return useMemo(() => ({ spaceId: type.spaceId, equipmentTypeId: type.id }), [type.spaceId, type.id]);
}
